'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import dynamic from 'next/dynamic'

import type { ImageSlotEntry } from '@/lib/content-edit/sources'

import { ContentEditToolbar } from './content-edit-toolbar'

/**
 * Loaded on demand, never at import time. A static import would put the drawer
 * (and through it the whole content-edit server-action module) into the module
 * graph of every client component that renders an editable primitive, which
 * reaches as far as the cart and the dashboard product tables. The drawer only
 * ever opens for an ADMIN on an allowlisted host, so no visitor pays for it.
 */
const ContentEditImageDrawer = dynamic(
  () => import('./content-edit-image-drawer').then((mod) => mod.ContentEditImageDrawer),
  { ssr: false }
)

/** Same reasoning as the image drawer above: on demand, never at import time. */
const ContentEditPageDrawer = dynamic(
  () => import('./content-edit-page-drawer').then((mod) => mod.ContentEditPageDrawer),
  { ssr: false }
)

/**
 * Client state for staging content edit mode.
 *
 * The provider wraps the whole app and renders `children` untouched. Edit mode
 * only turns on when GET /api/content-edit/status answers `enabled: true`, which
 * it does only for an ADMIN session on an allowlisted host with the server-only
 * flag set. Production answers `enabled: false`, so the toolbar never mounts and
 * every editable primitive stays in its plain render path.
 *
 * The status route is a UI convenience, not the security boundary: every save
 * action re-checks the gate and the role server-side.
 */

export interface ImageDrawerTarget {
  slot: string
  current: ImageSlotEntry | null
}

export interface ContentEditContextValue {
  enabled: boolean
  editing: boolean
  pending: number
  setEditing: (on: boolean) => void
  refreshStatus: () => Promise<void>
  openImageDrawer: (slot: string, current: ImageSlotEntry | null) => void
  openPageDrawer: (slug: string) => void
  /** Drawer targets. The provider owns them and renders the drawers. */
  imageDrawer: ImageDrawerTarget | null
  pageDrawer: string | null
  closeImageDrawer: () => void
  closePageDrawer: () => void
}

/** Everything off and every mutator a no-op, so `useContentEdit()` is safe outside the provider. */
const DISABLED: ContentEditContextValue = {
  enabled: false,
  editing: false,
  pending: 0,
  setEditing: () => {},
  refreshStatus: async () => {},
  openImageDrawer: () => {},
  openPageDrawer: () => {},
  imageDrawer: null,
  pageDrawer: null,
  closeImageDrawer: () => {},
  closePageDrawer: () => {}
}

const ContentEditContext = createContext<ContentEditContextValue>(DISABLED)

export function useContentEdit(): ContentEditContextValue {
  return useContext(ContentEditContext)
}

export function ContentEditProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(false)
  const [pending, setPending] = useState(0)
  const [editing, setEditing] = useState(false)
  const [imageDrawer, setImageDrawer] = useState<ImageDrawerTarget | null>(null)
  const [pageDrawer, setPageDrawer] = useState<string | null>(null)

  const refreshStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/content-edit/status', { cache: 'no-store' })
      if (!res.ok) return
      const body = (await res.json()) as { enabled?: unknown; pending?: unknown }
      const isEnabled = body.enabled === true
      setEnabled(isEnabled)
      setPending(typeof body.pending === 'number' ? body.pending : 0)
      if (!isEnabled) setEditing(false)
    } catch {
      // Fail closed and stay silent: a network blip must never light up the
      // toolbar, and a visitor must never see an error for a feature that is
      // not theirs.
    }
  }, [])

  useEffect(() => {
    void refreshStatus()
  }, [refreshStatus])

  const value = useMemo<ContentEditContextValue>(
    () => ({
      enabled,
      // `enabled` is the single switch: a stale `editing` cannot outlive it.
      editing: enabled && editing,
      pending,
      setEditing,
      refreshStatus,
      openImageDrawer: (slot: string, current: ImageSlotEntry | null) => setImageDrawer({ slot, current }),
      openPageDrawer: (slug: string) => setPageDrawer(slug),
      imageDrawer,
      pageDrawer,
      closeImageDrawer: () => setImageDrawer(null),
      closePageDrawer: () => setPageDrawer(null)
    }),
    [enabled, editing, pending, refreshStatus, imageDrawer, pageDrawer]
  )

  return (
    <ContentEditContext.Provider value={value}>
      {children}
      {enabled ? <ContentEditToolbar /> : null}
      {enabled && imageDrawer ? (
        <ContentEditImageDrawer
          key={imageDrawer.slot}
          slot={imageDrawer.slot}
          current={imageDrawer.current}
          onClose={() => setImageDrawer(null)}
        />
      ) : null}
      {enabled && pageDrawer ? (
        <ContentEditPageDrawer key={pageDrawer} slug={pageDrawer} onClose={() => setPageDrawer(null)} />
      ) : null}
    </ContentEditContext.Provider>
  )
}
